import React, { useEffect, useMemo, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Box,
  Typography,
  Grid,
  TextField,
  Switch,
  Button,
  Card,
  CardContent,
  Paper,
  ListItemButton,
  ListItemText,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  createDealerAction, clearCreateDealerAction,
  getDealerByIdAction, updateDealerAction, clearUpdateDealerAction, clearGetDealerByIdAction,
  getAllDistrictsAction, getAllMandalsAction, getAllVillagesAction,
  getAllZonesAction, getAllRegionsAction, getAllAreasAction,
} from "../../store/actions";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";


/* ── Search-or-Create Location Input (defined outside CreateDealer to avoid re-mount on every render) ── */
const LocationSearchInput = ({
  label, value, selectedId, suggestions,
  onTextChange, onSelect, disabled, error, helperText,
}) => {
  const [focused, setFocused] = useState(false);

  // Normalize: lowercase, remove all spaces
  const normalize = (str) => (str || '').toLowerCase().replace(/\s+/g, '');
  const filtered = useMemo(() => {
    if (!value?.trim()) return [];
    const inputNorm = normalize(value);
    return suggestions.filter((s) =>
      normalize(s.label).includes(inputNorm)
    );
  }, [value, suggestions]);

  const showSuggestions = focused && value?.trim().length > 0 && filtered.length > 0;
  const isNew = value?.trim().length > 0 && !selectedId;

  let displayHelper = "";
  let helperColor;
  if (error && helperText) {
    displayHelper = helperText;
    helperColor = "error.main";
  } else if (selectedId) {
    displayHelper = `✓ Existing ${label.toLowerCase()} selected`;
    helperColor = "success.main";
  } else if (isNew) {
    displayHelper = `New ${label.toLowerCase()} will be created`;
    helperColor = "info.main";
  }

  return (
    <Box sx={{ position: "relative" }}>
      <TextField
        fullWidth
        size="small"
        label={label}
        value={value}
        onChange={(e) => onTextChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setTimeout(() => setFocused(false), 200)}
        disabled={disabled}
        error={error}
        helperText={displayHelper}
        placeholder={`Search or create ${label.toLowerCase()}`}
        FormHelperTextProps={{ sx: { color: helperColor } }}
        sx={{
          "& .MuiOutlinedInput-root": {
            borderRadius: 2,
            backgroundColor: "#FAFAFA",
          },
        }}
      />
      {showSuggestions && (
        <Paper
          elevation={4}
          sx={{
            position: "absolute",
            zIndex: 1300,
            width: "100%",
            maxHeight: 200,
            overflow: "auto",
            mt: 0.5,
            borderRadius: 1,
          }}
        >
          {filtered.map((item) => (
            <ListItemButton
              key={item.id}
              onMouseDown={(e) => {
                e.preventDefault();
              }}
              onClick={() => {
                onSelect(item);
                setFocused(false);
              }}
              sx={{ py: 0.5, px: 1.5 }}
            >
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{ variant: "body2" }}
              />
            </ListItemButton>
          ))}
        </Paper>
      )}
    </Box>
  );
};


const CreateDealer = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const dealerId = location.state?.dealerId;
  const mode = location.state?.mode;
  const [formPopulated, setFormPopulated] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { successData: createData, error: createError, errorInfo: createErrorInfo } = useSelector((s) => s.createDealerReducer || {});
  const { successData: updateData, error: updateError, errorInfo: updateErrorInfo } = useSelector((s) => s.updateDealerReducer || {});
  const { successData: dealerByIdData, error: getByIdError, errorInfo: getByIdErrorInfo } = useSelector((s) => s.getDealerByIdReducer || {});
  const { successData: districtsData } = useSelector((s) => s.getAllDistrictsReducer || {});
  const { successData: mandalsData } = useSelector((s) => s.getAllMandalsReducer || {});
  const { successData: villagesData } = useSelector((s) => s.getAllVillagesReducer || {});
  const { successData: zonesData, loading: zonesLoading } = useSelector((s) => s.getAllZonesReducer || {});
  const { successData: regionsData, loading: regionsLoading } = useSelector((s) => s.getAllRegionsReducer || {});
  const { successData: areasData, loading: areasLoading } = useSelector((s) => s.getAllAreasReducer || {});

  const formik = useFormik({
    initialValues: {
      fullName: "",
      zoneId: "",
      regionId: "",
      areaId: "",
      districtName: "",
      districtId: "",
      mandalName: "",
      mandalId: "",
      villageName: "",
      villageId: "",
      active: true,
    },
    validationSchema: Yup.object({
      fullName: Yup.string().required("Dealer name is required"),
      zoneId: Yup.string().required("Zone is required"),
      regionId: Yup.string().required("Region is required"),
      areaId: Yup.string().required("Area is required"),
      districtName: Yup.string().required("District is required"),
      mandalName: Yup.string().required("Mandal is required"),
      villageName: Yup.string().required("Village is required"),
      active: Yup.boolean().required(),
    }),
    onSubmit: async (values) => {
      setIsSubmitting(true);

      // District: existing → send ID (number), new → send name (string)
      const districtValue = values.districtId
        ? values.districtId
        : values.districtName.trim();

      // Mandal: existing → send ID (number), new → send name (string)
      const mandalValue = values.mandalId
        ? values.mandalId
        : values.mandalName.trim();

      // Village: existing → send ID (number), new → send name (string)
      const villageValue = values.villageId
        ? values.villageId
        : values.villageName.trim();

      const dealerPayload = {
        name: values.fullName,
        zoneId: values.zoneId,
        regionId: values.regionId,
        areaId: values.areaId,
        districtId: districtValue,
        mandalId: mandalValue,
        villageId: villageValue,
        status: values.active ? "ACTIVE" : "INACTIVE",
      };

      try {
        if (mode === 'edit' && dealerId) {
          dispatch(updateDealerAction({ ...dealerPayload, endPoint: `dealer/${dealerId}` }));
        } else {
          dispatch(createDealerAction({ ...dealerPayload, endPoint: `dealer` }));
        }
      } catch (err) {
        toast.error(err?.message || "Failed to create dealer. Please try again.");
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  // Fetch dealer by id for edit
  useEffect(() => {
    if (mode === 'edit' && dealerId) {
      dispatch(clearGetDealerByIdAction());
      setFormPopulated(false);
      dispatch(getDealerByIdAction({ endPoint: `dealer/${dealerId}` }));
    }
  }, [dispatch, mode, dealerId]);

  // Load zones, regions, areas, districts, mandals, villages for selects
  useEffect(() => {
    dispatch(getAllZonesAction());
    dispatch(getAllRegionsAction());
    dispatch(getAllAreasAction());
    dispatch(getAllDistrictsAction());
    dispatch(getAllMandalsAction());
    dispatch(getAllVillagesAction());
  }, [dispatch]);

  // Populate form from fetched data
  useEffect(() => {
    if (mode === 'edit' && dealerByIdData && !formPopulated) {
      const d = dealerByIdData.data || dealerByIdData;
      // Find district, mandal, village objects
      const districtObj = districts.find(x => String(x.id) === String(d.districtId));
      const mandalObj = mandals.find(x => String(x.id) === String(d.mandalId));
      const villageObj = villages.find(x => String(x.id) === String(d.villageId));
      // Find area, region, zone from relationships
      let areaId = districtObj?.areaId || null;
      let regionId = null;
      let zoneId = null;
      if (areaId) {
        const areaObj = areas.find(x => String(x.id) === String(areaId));
        regionId = areaObj?.regionId || null;
        if (regionId) {
          const regionObj = regions.find(x => String(x.id) === String(regionId));
          zoneId = regionObj?.zoneId || null;
        }
      }
      formik.setValues({
        fullName: d?.name || d?.fullName || d?.dealerName || "",
        zoneId: zoneId || "",
        regionId: regionId || "",
        areaId: areaId || "",
        districtName: districtObj?.label || d?.districtName || "",
        districtId: d?.districtId || "",
        mandalName: mandalObj?.label || d?.mandalName || "",
        mandalId: d?.mandalId || "",
        villageName: villageObj?.label || d?.villageName || "",
        villageId: d?.villageId || "",
        active: d?.status === 'ACTIVE' || d?.active === true || d?.status === true,
      });
      setFormPopulated(true);
    }
  }, [dealerByIdData, mode, formPopulated]);

  // Toast on getById failure
  useEffect(() => {
    if (mode === 'edit' && getByIdError) {
      toast.error(getByIdErrorInfo || 'Failed to load dealer details');
    }
  }, [mode, getByIdError, getByIdErrorInfo]);

  // Handle create/update success & errors
  useEffect(() => {
    if (mode === 'edit') {
      if (updateData) {
        toast.success('Dealer updated successfully');
        dispatch(clearUpdateDealerAction());
        navigate('/masterData/dealer');
      }
      if (updateError) {
        toast.error(updateErrorInfo || 'Failed to update dealer');
        dispatch(clearUpdateDealerAction());
      }
    } else {
      if (createData) {
        toast.success('Dealer created successfully');
        dispatch(clearCreateDealerAction());
        formik.resetForm();
        navigate('/masterData/dealer');
      }
      if (createError) {
        toast.error(createErrorInfo || 'Failed to create dealer');
        dispatch(clearCreateDealerAction());
      }
    }
  }, [mode, updateData, updateError, updateErrorInfo, createData, createError, createErrorInfo, dispatch, navigate]);

  // Normalize lists and build dependent options
  const rawToArray = (data, innerKey) => {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    // Try data.data[innerKey]
    if (innerKey && data?.data && Array.isArray(data.data[innerKey])) return data.data[innerKey];
    // Try data[innerKey]
    if (innerKey && Array.isArray(data[innerKey])) return data[innerKey];
    // Try data.data as array
    if (data?.data && Array.isArray(data.data)) return data.data;
    // Try data.results or data.items (common API patterns)
    if (Array.isArray(data?.results)) return data.results;
    if (Array.isArray(data?.items)) return data.items;
    return [];
  };

  const zonesRaw = useMemo(() => rawToArray(zonesData, 'zones'), [zonesData]);
  const regionsRaw = useMemo(() => rawToArray(regionsData, 'regions'), [regionsData]);
  const areasRaw = useMemo(() => rawToArray(areasData, 'areas'), [areasData]);
  const districtsRaw = useMemo(() => rawToArray(districtsData, 'districts'), [districtsData]);
  const mandalsRaw = useMemo(() => rawToArray(mandalsData, 'mandals'), [mandalsData]);
  const villagesRaw = useMemo(() => rawToArray(villagesData, 'villages'), [villagesData]);

  const zones = useMemo(() => zonesRaw.map(z => ({
    id: z?.zoneId || z?.zone_id || z?.id || z?._id || z?.value,
    label: z?.name || z?.zoneName || z?.zone_name || z?.label || '',
  })).filter(z => z.id && z.label), [zonesRaw]);

  const regions = useMemo(() => regionsRaw.map(r => ({
    id: r?.regionId || r?.region_id || r?.id || r?._id || r?.value,
    label: r?.name || r?.regionName || r?.region_name || r?.label || '',
    zoneId: r?.zoneId || r?.zone_id || r?.zone?.zoneId || r?.zone?.zone_id || r?.zone || null,
  })).filter(r => r.id && r.label), [regionsRaw]);

  const areas = useMemo(() => areasRaw.map(a => ({
    id: a?.areaId || a?.area_id || a?.id || a?._id || a?.value,
    label: a?.name || a?.areaName || a?.area_name || a?.label || '',
    regionId: a?.regionId || a?.region_id || a?.region?.regionId || a?.region?.region_id || a?.region || null,
  })).filter(a => a.id && a.label), [areasRaw]);

  const districts = useMemo(() => districtsRaw.map(d => ({
    id: d?.districtId || d?.district_id || d?.id || d?._id || d?.value,
    label: d?.name || d?.districtName || d?.district_name || d?.label || '',
    areaId: d?.areaId || d?.area_id || d?.area?.areaId || d?.area?.area_id || d?.area || null,
  })).filter(d => d.id && d.label), [districtsRaw]);

  const mandals = useMemo(() => mandalsRaw.map(m => ({
    id: m?.mandalId || m?.mandal_id || m?.id || m?._id || m?.value,
    label: m?.name || m?.mandalName || m?.mandal_name || m?.label || '',
    districtId: m?.districtId || m?.district_id || m?.district?.districtId || m?.district?.district_id || m?.district || null,
  })).filter(m => m.id && m.label), [mandalsRaw]);

  const villages = useMemo(() => villagesRaw.map(v => ({
    id: v?.villageId || v?.village_id || v?.id || v?._id || v?.value,
    label: v?.name || v?.villageName || v?.village_name || v?.label || '',
    mandalId: v?.mandalId || v?.mandal_id || v?.mandal?.mandalId || v?.mandal?.mandal_id || v?.mandal || null,
  })).filter(v => v.id && v.label), [villagesRaw]);

  const filteredRegions = useMemo(() => {
    if (!formik.values.zoneId) return regions;
    return regions.filter(r => String(r.zoneId) === String(formik.values.zoneId));
  }, [regions, formik.values.zoneId]);

  const filteredAreas = useMemo(() => {
    if (!formik.values.regionId) return areas;
    return areas.filter(a => String(a.regionId) === String(formik.values.regionId));
  }, [areas, formik.values.regionId]);

  /* Suggestions for search-or-create location inputs (strict hierarchy) */
  const districtSuggestions = useMemo(() => {
    if (!formik.values.areaId) return [];
    return districts.filter(d => !d.areaId || String(d.areaId) === String(formik.values.areaId));
  }, [districts, formik.values.areaId]);

  const mandalSuggestions = useMemo(() => {
    if (!formik.values.districtId) return [];
    return mandals.filter(m => !m.districtId || String(m.districtId) === String(formik.values.districtId));
  }, [mandals, formik.values.districtId]);

  const villageSuggestions = useMemo(() => {
    if (!formik.values.mandalId) return [];
    return villages.filter(v => !v.mandalId || String(v.mandalId) === String(formik.values.mandalId));
  }, [villages, formik.values.mandalId]);

  const filterOptions = createFilterOptions({
    matchFrom: 'any',
    stringify: (option) => option.label,
    trim: true,
    ignoreCase: true,
    ignoreAccents: true,
  });

  return (
  
      <Box sx={{minHeight:'100vh',mt:{sm:7,xs:0,md:0},width:'100%'}}>
        <Card variant="outlined" sx={{ borderRadius: 2 ,width:'100%'}}>
          <CardContent>

            {/* Header */}
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", sm: "flex-start", md: "center" }}
              flexDirection={{ xs: "column", sm: "row" }}
              mb={3}
            >
              <Box>
                <Typography variant="h6" fontWeight={600}>
                  {mode === 'edit' ? 'Edit Dealer' : 'Add Dealer'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Create and manage dealer profile
                </Typography>
              </Box>

              {/* Status */}
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="body2">Status</Typography>
                <Switch
                  checked={formik.values.active}
                  onChange={(e) =>
                    formik.setFieldValue("active", e.target.checked)
                  }
                  color="success"
                />
                <Typography
                  variant="body2"
                  color={formik.values.active ? "success.main" : "text.secondary"}
                >
                  {formik.values.active ? "Active" : "Inactive"}
                </Typography>
              </Box>
            </Box>

            {/* Basic Information */}

            <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={3}>
              {/* Dealer Full Name */}
              <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Dealer Name"
                  name="fullName"
                  placeholder="Enter dealer name"
                  value={formik.values.fullName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.fullName && Boolean(formik.errors.fullName)}
                  helperText={formik.touched.fullName && formik.errors.fullName}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: '#FAFAFA',
                    }
                  }}
                />
              </Grid>

              {/* Zone */}
              <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                <Autocomplete
                  options={zones}
                  
                  loading={zonesLoading}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      backgroundColor: "#FAFAFA"
                    }
                  }}
                  value={zones.find(z => z.id === formik.values.zoneId) || null}
                  onChange={(e, val) => {
                    formik.setFieldValue("zoneId", val ? val.id : "");
                    formik.setFieldValue("regionId", "");
                    formik.setFieldValue("areaId", "");
                    formik.setFieldValue("districtName", "");
                    formik.setFieldValue("districtId", "");
                    formik.setFieldValue("mandalName", "");
                    formik.setFieldValue("mandalId", "");
                    formik.setFieldValue("villageName", "");
                    formik.setFieldValue("villageId", "");
                  }}
                  getOptionLabel={option => option?.label || ""}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  filterOptions={filterOptions}
                  clearOnEscape
                  renderInput={params => (
                    <TextField
                      {...params}
                      fullWidth
                      size="small"
                      label="Zone"
                      name="zoneId"
                      onBlur={() => formik.setFieldTouched("zoneId", true)}
                      error={formik.touched.zoneId && Boolean(formik.errors.zoneId)}
                      helperText={formik.touched.zoneId && formik.errors.zoneId}
                    />
                  )}
                />
              </Grid>

              {/* Region */}
              <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                <Autocomplete
                  options={filteredRegions}
                  loading={regionsLoading}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      backgroundColor: "#FAFAFA"
                    }
                  }}
                  value={filteredRegions.find(r => r.id === formik.values.regionId) || null}
                  onChange={(e, val) => {
                    formik.setFieldValue("regionId", val ? val.id : "");
                    formik.setFieldValue("areaId", "");
                    formik.setFieldValue("districtName", "");
                    formik.setFieldValue("districtId", "");
                    formik.setFieldValue("mandalName", "");
                    formik.setFieldValue("mandalId", "");
                    formik.setFieldValue("villageName", "");
                    formik.setFieldValue("villageId", "");
                  }}
                  getOptionLabel={option => option?.label || ""}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  filterOptions={filterOptions}
                  clearOnEscape
                  renderInput={params => (
                    <TextField
                      {...params}
                      fullWidth
                      size="small"
                      label="Region"
                      name="regionId"
                      onBlur={() => formik.setFieldTouched("regionId", true)}
                      error={formik.touched.regionId && Boolean(formik.errors.regionId)}
                      helperText={formik.touched.regionId && formik.errors.regionId}
                    />
                  )}
                />
              </Grid>

              {/* Area */}
              <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                <Autocomplete
                  options={filteredAreas}
                  loading={areasLoading}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      backgroundColor: "#FAFAFA"
                    }
                  }}
                  value={filteredAreas.find(a => a.id === formik.values.areaId) || null}
                  onChange={(e, val) => {
                    formik.setFieldValue("areaId", val ? val.id : "");
                    formik.setFieldValue("districtName", "");
                    formik.setFieldValue("districtId", "");
                    formik.setFieldValue("mandalName", "");
                    formik.setFieldValue("mandalId", "");
                    formik.setFieldValue("villageName", "");
                    formik.setFieldValue("villageId", "");
                  }}
                  getOptionLabel={option => option?.label || ""}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  filterOptions={filterOptions}
                  clearOnEscape
                  renderInput={params => (
                    <TextField
                      {...params}
                      fullWidth
                      size="small"
                      label="Area"
                      name="areaId"
                      onBlur={() => formik.setFieldTouched("areaId", true)}
                      error={formik.touched.areaId && Boolean(formik.errors.areaId)}
                      helperText={formik.touched.areaId && formik.errors.areaId}
                    />
                  )}
                />
              </Grid>

              {/* District (search-or-create) */}
              <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                <LocationSearchInput
                  label="District"
                  value={formik.values.districtName}
                  selectedId={formik.values.districtId}
                  suggestions={districtSuggestions}
                  disabled={!formik.values.areaId}
                  error={formik.touched.districtName && Boolean(formik.errors.districtName)}
                  helperText={formik.touched.districtName && formik.errors.districtName}
                  onTextChange={(text) => {
                    formik.setFieldValue("districtName", text);
                    // Normalize: lowercase, remove all spaces
                    const normalize = (str) => (str || '').toLowerCase().replace(/\s+/g, '');
                    const inputNorm = normalize(text);
                    const districtMatch = districtSuggestions.find(
                      (s) => normalize(s.label) === inputNorm
                    );
                    formik.setFieldValue("districtId", districtMatch ? districtMatch.id : "");
                    formik.setFieldTouched("districtName", true, false);
                    formik.setFieldValue("mandalName", "");
                    formik.setFieldValue("mandalId", "");
                    formik.setFieldValue("villageName", "");
                    formik.setFieldValue("villageId", "");
                  }}
                  onSelect={(item) => {
                    formik.setFieldValue("districtName", item.label);
                    formik.setFieldValue("districtId", item.id);
                    formik.setFieldTouched("districtName", true, false);
                    formik.setFieldValue("mandalName", "");
                    formik.setFieldValue("mandalId", "");
                    formik.setFieldValue("villageName", "");
                    formik.setFieldValue("villageId", "");
                  }}
                />
              </Grid>

              {/* Mandal (search-or-create) */}
              <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                <LocationSearchInput
                  label="Mandal"
                  value={formik.values.mandalName}
                  selectedId={formik.values.mandalId}
                  suggestions={mandalSuggestions}
                  disabled={!formik.values.districtName?.trim()}
                  error={formik.touched.mandalName && Boolean(formik.errors.mandalName)}
                  helperText={formik.touched.mandalName && formik.errors.mandalName}
                  onTextChange={(text) => {
                    formik.setFieldValue("mandalName", text);
                    const normalize = (str) => (str || '').toLowerCase().replace(/\s+/g, '');
                    const inputNorm = normalize(text);
                    const mandalMatch = mandalSuggestions.find(
                      (s) => normalize(s.label) === inputNorm
                    );
                    formik.setFieldValue("mandalId", mandalMatch ? mandalMatch.id : "");
                    formik.setFieldTouched("mandalName", true, false);
                    formik.setFieldValue("villageName", "");
                    formik.setFieldValue("villageId", "");
                  }}
                  onSelect={(item) => {
                    formik.setFieldValue("mandalName", item.label);
                    formik.setFieldValue("mandalId", item.id);
                    formik.setFieldTouched("mandalName", true, false);
                    formik.setFieldValue("villageName", "");
                    formik.setFieldValue("villageId", "");
                  }}
                />
              </Grid>

              {/* Village (search-or-create) */}
              <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                <LocationSearchInput
                  label="Village"
                  value={formik.values.villageName}
                  selectedId={formik.values.villageId}
                  suggestions={villageSuggestions}
                  disabled={!formik.values.mandalName?.trim()}
                  error={formik.touched.villageName && Boolean(formik.errors.villageName)}
                  helperText={formik.touched.villageName && formik.errors.villageName}
                  onTextChange={(text) => {
                    formik.setFieldValue("villageName", text);
                    const normalize = (str) => (str || '').toLowerCase().replace(/\s+/g, '');
                    const inputNorm = normalize(text);
                    const villageMatch = villageSuggestions.find(
                      (s) => normalize(s.label) === inputNorm
                    );
                    formik.setFieldValue("villageId", villageMatch ? villageMatch.id : "");
                    formik.setFieldTouched("villageName", true, false);
                  }}
                  onSelect={(item) => {
                    formik.setFieldValue("villageName", item.label);
                    formik.setFieldValue("villageId", item.id);
                    formik.setFieldTouched("villageName", true, false);
                  }}
                />
              </Grid>

            </Grid>

            {/* Buttons */}
            <Box
              display="flex"
              justifyContent="flex-end"
              gap={2}
              mt={4}
              flexDirection={{ xs: "column", sm: "row" }}
            >
              <Button
                variant="outlined"
                disabled={isSubmitting}
                onClick={() => {
                  formik.resetForm();
                  navigate('/masterData/dealer');
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                type="submit"
                disabled={isSubmitting}
                sx={{ backgroundColor: "#4F46E5" }}
              >
                {isSubmitting ? 'Saving…' : mode === 'edit' ? 'Update Dealer' : 'Save Dealer'}
              </Button>
            </Box>
            </form>
          </CardContent>
        </Card>
        </Box>

   
  );
};

export default CreateDealer;
